import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API, BACKEND_URL };

// Add viewer token to requests automatically
axios.interceptors.request.use((config) => {
  const viewerToken = localStorage.getItem('viewer_token');
  if (viewerToken && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${viewerToken}`;
  }
  return config;
});

// ============== API HELPERS ==============
export const api = {
  getSports: () => axios.get(`${API}/sports`),
  getLeagues: (sportId) => axios.get(`${API}/leagues${sportId ? `?sport_id=${sportId}` : ''}`),
  getTeams: (leagueId) => axios.get(`${API}/teams${leagueId ? `?league_id=${leagueId}` : ''}`),
  getGames: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return axios.get(`${API}/games${query.toString() ? `?${query.toString()}` : ''}`);
  },
  getGame: (id) => axios.get(`${API}/games/${id}`),
  getLiveGames: () => axios.get(`${API}/games/live`),
  getFeaturedGames: () => axios.get(`${API}/games/featured`),
  getUpcomingGames: (limit = 20) => axios.get(`${API}/games/upcoming?limit=${limit}`),
  getReplayGames: (limit = 20) => axios.get(`${API}/games/replays?limit=${limit}`),
  getLiveTVGames: () => axios.get(`${API}/games/live-tv`),
  getGameLiveScore: (id) => axios.get(`${API}/games/${id}/live-score`),
  getFixtures: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return axios.get(`${API}/fixtures${query.toString() ? `?${query.toString()}` : ''}`);
  },
  search: (q) => axios.get(`${API}/search`, { params: { q } }),
  getStats: () => axios.get(`${API}/stats`),
  
  // Admin APIs
  createSport: (data) => axios.post(`${API}/admin/sports`, data),
  updateSport: (id, data) => axios.put(`${API}/admin/sports/${id}`, data),
  deleteSport: (id) => axios.delete(`${API}/admin/sports/${id}`),
  createLeague: (data) => axios.post(`${API}/admin/leagues`, data),
  updateLeague: (id, data) => axios.put(`${API}/admin/leagues/${id}`, data),
  deleteLeague: (id) => axios.delete(`${API}/admin/leagues/${id}`),
  createTeam: (data) => axios.post(`${API}/admin/teams`, data),
  updateTeam: (id, data) => axios.put(`${API}/admin/teams/${id}`, data),
  deleteTeam: (id) => axios.delete(`${API}/admin/teams/${id}`),
  
  // Partner APIs
  getPartners: (isActive = null) => axios.get(`${API}/partners${isActive !== null ? `?is_active=${isActive}` : ''}`),
  createPartner: (data) => axios.post(`${API}/admin/partners`, data),
  updatePartner: (id, data) => axios.put(`${API}/admin/partners/${id}`, data),
  deletePartner: (id) => axios.delete(`${API}/admin/partners/${id}`),
  
  // Page Banners APIs
  getPageBanners: () => axios.get(`${API}/page-banners`),
  getPageBanner: (slug) => axios.get(`${API}/page-banners/${slug}`),
  updatePageBanner: (slug, data) => axios.put(`${API}/admin/page-banners/${slug}`, data),
  initPageBanners: () => axios.post(`${API}/admin/page-banners/init`),
  
  // Game Management
  createGame: (data) => axios.post(`${API}/admin/games`, data),
  updateGame: (id, data) => axios.put(`${API}/admin/games/${id}`, data),
  deleteGame: (id) => axios.delete(`${API}/admin/games/${id}`),
  
  // Fixture Management
  createFixture: (data) => axios.post(`${API}/admin/fixtures`, data),
  updateFixture: (id, data) => axios.put(`${API}/admin/fixtures/${id}`, data),
  deleteFixture: (id) => axios.delete(`${API}/admin/fixtures/${id}`),
  
  // Live Score Management (OLD - deprecated)
  createLiveScore: (data) => axios.post(`${API}/admin/live-scores`, data),
  updateLiveScore: (id, data) => axios.put(`${API}/admin/live-scores/${id}`, data),
  deleteLiveScore: (id) => axios.delete(`${API}/admin/live-scores/${id}`),
  
  // Score Control Panel APIs (NEW - unified with games)
  getGamesForScoreControl: (showAll = false) => axios.get(`${API}/admin/games/live-control${showAll ? '?show_all=true' : ''}`),
  updateGameScore: (id, data) => axios.put(`${API}/admin/games/${id}/score`, data),
  
  // Database Seed
  seedDatabase: () => axios.post(`${API}/admin/seed`),
  
  // Media Library APIs
  getMedia: (tag) => axios.get(`${API}/admin/media${tag ? `?tag=${tag}` : ''}`),
  uploadMedia: (formData) => axios.post(`${API}/admin/media/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateMedia: (id, data) => axios.put(`${API}/admin/media/${id}`, null, { params: data }),
  deleteMedia: (id) => axios.delete(`${API}/admin/media/${id}`),
  
  // Site Settings APIs
  getSettings: () => axios.get(`${API}/settings`),
  updateSettings: (data) => axios.put(`${API}/admin/settings`, data),
  
  // Club Management APIs
  getClubs: () => axios.get(`${API}/clubs`),
  getClub: (id) => axios.get(`${API}/clubs/${id}`),
  createClub: (data) => axios.post(`${API}/clubs`, data),
  updateClub: (id, data) => axios.put(`${API}/clubs/${id}`, data),
  deleteClub: (id) => axios.delete(`${API}/clubs/${id}`),
  getClubMatches: (clubId) => axios.get(`${API}/clubs/${clubId}/matches`),
  createClubMatch: (clubId, data) => axios.post(`${API}/clubs/${clubId}/matches`, data),
  updateClubMatch: (clubId, matchId, data) => axios.put(`${API}/clubs/${clubId}/matches/${matchId}`, data),
  deleteClubMatch: (clubId, matchId) => axios.delete(`${API}/clubs/${clubId}/matches/${matchId}`),
  generateMatchGraphic: (clubId, matchId, eventType) => 
    axios.post(`${API}/clubs/${clubId}/matches/${matchId}/graphics`, { event_type: eventType }),
  getMatchGraphics: (clubId, matchId) => axios.get(`${API}/clubs/${clubId}/matches/${matchId}/graphics`),
  generatePromoGraphic: (gameId, format) => axios.post(`${API}/admin/games/${gameId}/promo-graphic`, { format }),
  
  // Team Names autocomplete
  searchTeamNames: (query) => axios.get(`${API}/team-names/search?q=${encodeURIComponent(query)}`),
  addTeamName: (name) => axios.post(`${API}/team-names`, { name }),
  
  // Auth APIs
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
  verifyEmail: (token) => axios.post(`${API}/auth/verify-email?token=${token}`),
  getMe: () => axios.get(`${API}/auth/me`),
  requestPasswordReset: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, new_password: password }),
  
  // Match Center APIs
  getMatch: (clubId, matchId) => axios.get(`${API}/clubs/${clubId}/matches/${matchId}`),
  createMatch: (clubId, data) => axios.post(`${API}/clubs/${clubId}/matches`, data),
  getPlayers: (clubId) => axios.get(`${API}/clubs/${clubId}/players`),
  createPlayer: (clubId, data) => axios.post(`${API}/clubs/${clubId}/players`, data),
  deletePlayer: (clubId, playerId) => axios.delete(`${API}/clubs/${clubId}/players/${playerId}`),
  updateMatchStatus: (clubId, matchId, status) => axios.put(`${API}/clubs/${clubId}/matches/${matchId}/status`, { status }),
  addGoal: (clubId, matchId, data) => axios.post(`${API}/clubs/${clubId}/matches/${matchId}/goals`, data),
  removeGoal: (clubId, matchId, goalId) => axios.delete(`${API}/clubs/${clubId}/matches/${matchId}/goals/${goalId}`),
  editScore: (clubId, matchId, data) => axios.put(`${API}/clubs/${clubId}/matches/${matchId}/score`, data),
  addMatchEvent: (clubId, matchId, data) => axios.post(`${API}/clubs/${clubId}/matches/${matchId}/events`, data),
  generateGraphic: (clubId, matchId, eventType) => axios.post(`${API}/clubs/${clubId}/matches/${matchId}/graphics`, { event_type: eventType }),
  
  // Upcoming Streams
  getUpcomingStreams: () => axios.get(`${API}/admin/upcoming-streams`),
  
  // Nearby Matches (within 72 hours)
  getNearbyMatches: () => axios.get(`${API}/matches/nearby`),
  
  // News APIs
  getNews: (params = {}) => {
    const query = new URLSearchParams();
    if (params.league_id) query.append('league_id', params.league_id);
    if (params.team_id) query.append('team_id', params.team_id);
    if (params.limit) query.append('limit', params.limit);
    return axios.get(`${API}/news${query.toString() ? `?${query.toString()}` : ''}`);
  },
  getNewsArticle: (slug) => axios.get(`${API}/news/${slug}`),
  getNewsAdmin: () => axios.get(`${API}/admin/news`),
  createNews: (data) => axios.post(`${API}/admin/news`, data),
  updateNews: (id, data) => axios.put(`${API}/admin/news/${id}`, data),
  deleteNews: (id) => axios.delete(`${API}/admin/news/${id}`),
  
  // Podcast APIs
  getPodcastEpisodes: (limit = 50) => axios.get(`${API}/podcast/episodes?limit=${limit}`),
  getPodcastEpisode: (slug) => axios.get(`${API}/podcast/episodes/${slug}`),
  getPodcastRSS: () => `${API}/podcast/rss`,
  getPodcastEpisodesAdmin: () => axios.get(`${API}/admin/podcast/episodes`),
  createPodcastEpisode: (data) => axios.post(`${API}/admin/podcast/episodes`, data),
  updatePodcastEpisode: (id, data) => axios.put(`${API}/admin/podcast/episodes/${id}`, data),
  deletePodcastEpisode: (id) => axios.delete(`${API}/admin/podcast/episodes/${id}`),
  
  // Newsletter
  subscribeNewsletter: (email) => axios.post(`${API}/newsletter/subscribe`, { email }),
  
  // Locations
  getLocations: () => axios.get(`${API}/locations`),
  createLocation: (data) => axios.post(`${API}/admin/locations`, data),
  deleteLocation: (id) => axios.delete(`${API}/admin/locations/${id}`),
  
  // Ad Platform Admin APIs
  getAdvertisers: () => axios.get(`${API}/admin/advertisers`),
  getAdPricing: () => axios.get(`${API}/admin/ad-pricing`),
  updateAdPricing: (data) => axios.put(`${API}/admin/ad-pricing`, data),
  getManualSponsors: () => axios.get(`${API}/admin/manual-sponsors`),
  createManualSponsor: (data) => axios.post(`${API}/admin/manual-sponsors`, data),
  updateManualSponsor: (id, data) => axios.put(`${API}/admin/manual-sponsors/${id}`, data),
  deleteManualSponsor: (id) => axios.delete(`${API}/admin/manual-sponsors/${id}`),

  // Mux Streaming
  createMuxStream: (gameId, reducedLatency = true) => axios.post(`${API}/admin/mux/streams`, { game_id: gameId, reduced_latency: reducedLatency }),
  getMuxStreamStatus: (gameId) => axios.get(`${API}/admin/mux/streams/${gameId}`),
  enableMuxStream: (gameId) => axios.put(`${API}/admin/mux/streams/${gameId}/enable`),
  disableMuxStream: (gameId) => axios.put(`${API}/admin/mux/streams/${gameId}/disable`),
  completeMuxStream: (gameId) => axios.put(`${API}/admin/mux/streams/${gameId}/complete`),
  resetMuxStreamKey: (gameId) => axios.post(`${API}/admin/mux/streams/${gameId}/reset-key`),
  deleteMuxStream: (gameId) => axios.delete(`${API}/admin/mux/streams/${gameId}`),
  addSimulcastTarget: (gameId, data) => axios.post(`${API}/admin/mux/streams/${gameId}/simulcast`, data),
  removeSimulcastTarget: (gameId, targetId) => axios.delete(`${API}/admin/mux/streams/${gameId}/simulcast/${targetId}`),
  getMuxAssets: (gameId) => axios.get(`${API}/admin/mux/assets/${gameId}`),
  getWatchData: (gameId) => axios.get(`${API}/watch/${gameId}`),
  getPlaybackToken: (gameId) => axios.get(`${API}/watch/${gameId}/playback-token`, { withCredentials: true }),

  // Viewer Auth
  viewerRegister: (data) => axios.post(`${API}/viewer-auth/register`, data, { withCredentials: true }),
  viewerLogin: (data) => axios.post(`${API}/viewer-auth/login`, data, { withCredentials: true }),
  viewerLogout: () => axios.post(`${API}/viewer-auth/logout`, {}, { withCredentials: true }),
  viewerMe: () => axios.get(`${API}/viewer-auth/me`, { withCredentials: true }),
  viewerUpdateProfile: (data) => axios.put(`${API}/viewer-auth/profile`, data, { withCredentials: true }),
  viewerGoogleCallback: (data) => axios.post(`${API}/viewer-auth/google-callback`, data, { withCredentials: true }),
  checkGameAccess: (gameId) => axios.get(`${API}/viewer-auth/check-access/${gameId}`, { withCredentials: true }),
  viewerForgotPassword: (email) => axios.post(`${API}/viewer-auth/forgot-password`, { email }),
  viewerResetPassword: (token, new_password) => axios.post(`${API}/viewer-auth/reset-password`, { token, new_password }),

  // Subscriptions & PPV
  getPlans: () => axios.get(`${API}/subscriptions/plans`),
  subscriptionCheckout: (data) => axios.post(`${API}/subscriptions/checkout`, data, { withCredentials: true }),
  // Admin Subscription Plans
  getAdminPlans: () => axios.get(`${API}/admin/subscription-plans`),
  createPlan: (data) => axios.post(`${API}/admin/subscription-plans`, data),
  updatePlan: (id, data) => axios.put(`${API}/admin/subscription-plans/${id}`, data),
  deletePlan: (id) => axios.delete(`${API}/admin/subscription-plans/${id}`),
  ppvCheckout: (data) => axios.post(`${API}/ppv/checkout`, data, { withCredentials: true }),
  checkPaymentStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true }),

  // Chat
  getChatMessages: (gameId, limit = 50) => axios.get(`${API}/chat/${gameId}/messages`, { params: { limit } }),
  getChatStats: (gameId) => axios.get(`${API}/chat/${gameId}/stats`),

  // Bulk Import
  bulkValidate: (type, formData) => axios.post(`${API}/admin/bulk/validate/${type}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bulkImport: (type, formData) => axios.post(`${API}/admin/bulk/import/${type}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Booking
  getBookingVenues: (sport) => axios.get(`${API}/booking/venues`, { params: sport ? { sport } : {} }),
  getFleetTypes: () => axios.get(`${API}/booking/pricing/fleet-types`),
  calculateBookingPrice: (data) => axios.post(`${API}/booking/pricing/calculate`, data),

  // Channels
  getChannels: () => axios.get(`${API}/admin/channels`),
  createChannel: (data) => axios.post(`${API}/admin/channels`, data),
  updateChannel: (id, data) => axios.put(`${API}/admin/channels/${id}`, data),
  deleteChannel: (id) => axios.delete(`${API}/admin/channels/${id}`),
  getChannelStatus: (id) => axios.get(`${API}/admin/channels/${id}/status`),
  assignChannel: (id, data) => axios.post(`${API}/admin/channels/${id}/assign`, data),
  forceAssignChannel: (id, data) => axios.post(`${API}/admin/channels/${id}/force-assign`, data),
  unassignChannel: (id) => axios.post(`${API}/admin/channels/${id}/unassign`),
  enableChannel: (id) => axios.post(`${API}/admin/channels/${id}/enable`),
  disableChannel: (id) => axios.post(`${API}/admin/channels/${id}/disable`),
  scheduleChannelStop: (id, data) => axios.post(`${API}/admin/channels/${id}/schedule-stop`, data),
  cancelChannelSchedule: (id) => axios.post(`${API}/admin/channels/${id}/cancel-schedule`),

  // Viewer Preferences
  getPreferences: () => axios.get(`${API}/viewer/preferences`),
  updatePreferences: (data) => axios.put(`${API}/viewer/preferences`, data),
  checkPreferencesSetup: () => axios.get(`${API}/viewer/preferences/has-setup`),
  getPersonalizedFeed: () => axios.get(`${API}/games/personalized-feed`),

  // Analytics
  startView: (data) => axios.post(`${API}/analytics/view`, data),
  sendHeartbeat: (data) => axios.post(`${API}/analytics/heartbeat`, data),
  endView: (data) => axios.post(`${API}/analytics/end`, data),
  getPublicGameStats: (gameId) => axios.get(`${API}/analytics/game/${gameId}/public`),
  getAnalyticsOverview: () => axios.get(`${API}/admin/analytics/overview`),
  getGameAnalytics: (gameId) => axios.get(`${API}/admin/analytics/game/${gameId}`),
  getViewerAnalytics: () => axios.get(`${API}/admin/analytics/viewers`),
  getEngagementScores: () => axios.get(`${API}/analytics/engagement-scores`),

  // Device Sessions & Enforcement
  sessionHeartbeat: () => axios.post(`${API}/viewer-auth/session/heartbeat`),
  getViewerSessions: () => axios.get(`${API}/viewer-auth/sessions`),
  revokeSession: (sessionId) => axios.delete(`${API}/viewer-auth/sessions/${sessionId}`),
  checkCommercialIP: () => axios.post(`${API}/viewer-auth/check-ip`),

  // Admin: Known IPs
  getKnownIPs: () => axios.get(`${API}/admin/known-ips`),
  addKnownIP: (data) => axios.post(`${API}/admin/known-ips`, data),
  updateKnownIP: (id, data) => axios.put(`${API}/admin/known-ips/${id}`, data),
  deleteKnownIP: (id) => axios.delete(`${API}/admin/known-ips/${id}`),

  // Admin: Bans
  getBans: () => axios.get(`${API}/admin/bans`),
  unbanViewer: (viewerId) => axios.post(`${API}/admin/unban/${viewerId}`),
  unbanIP: (ipId) => axios.post(`${API}/admin/unban-ip/${ipId}`),
  banViewer: (viewerId, reason) => axios.post(`${API}/admin/ban-viewer/${viewerId}`, { reason }),
  banIP: (ip, reason) => axios.post(`${API}/admin/ban-ip`, { ip_address: ip, reason }),

  // Admin: Environment Tracking
  getEnvironmentLogs: (params = {}) => {
    const query = new URLSearchParams();
    if (params.viewer_id) query.append('viewer_id', params.viewer_id);
    if (params.suspicious_only) query.append('suspicious_only', 'true');
    return axios.get(`${API}/admin/environment-logs${query.toString() ? `?${query.toString()}` : ''}`);
  },
  getSuspiciousViewers: () => axios.get(`${API}/admin/suspicious-viewers`),

  // Live Channel
  getChannelNow: () => axios.get(`${API}/channel/now`),
  getChannelSchedule: (date) => axios.get(`${API}/channel/schedule${date ? `?date=${date}` : ''}`),
  adminGetChannelSchedule: () => axios.get(`${API}/admin/channel/schedule`),
  adminAddScheduleItem: (data) => axios.post(`${API}/admin/channel/schedule`, data),
  adminUpdateScheduleItem: (id, data) => axios.put(`${API}/admin/channel/schedule/${id}`, data),
  adminDeleteScheduleItem: (id) => axios.delete(`${API}/admin/channel/schedule/${id}`),
  adminForcePlay: (data) => axios.post(`${API}/admin/channel/force-play`, data),
  adminGetPromos: () => axios.get(`${API}/admin/channel/promos`),
  adminAddPromo: (data) => axios.post(`${API}/admin/channel/promos`, data),
  adminDeletePromo: (id) => axios.delete(`${API}/admin/channel/promos/${id}`),

  // TV Activation
  tvRequestCode: () => axios.post(`${API}/viewer-auth/tv/request-code`),
  tvPoll: (deviceToken) => axios.get(`${API}/viewer-auth/tv/poll/${deviceToken}`),
  tvActivate: (code) => axios.post(`${API}/viewer-auth/tv/activate`, { code }),
  tvVerify: (token) => axios.post(`${API}/viewer-auth/tv/verify`, {}, { headers: { Authorization: `Bearer ${token}` } }),
};

export default api;
